import { Box, Button, Center, Container, Group, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { WorkPackageStatus } from '@/api/generated/models/workPackageStatus';
import { useListObjects } from '@/api/generated/objects/objects';
import { useListOrganisationConnections } from '@/api/generated/organisation-connections/organisation-connections';
import { useSearchAllWorkPackages } from '@/api/generated/work-structure/work-structure';
import {
  CardSection,
  ListPagination,
  MobileFab,
  MobileListFilters,
  PageHeading,
  RowActionsMenu,
  SearchField,
  SelectField,
  StatusBanner,
  StatusPill,
  TABLE_ACTION_COL_WIDTH,
  TABLE_CODE_COL_WIDTH,
  TABLE_OBJECT_COL_WIDTH,
  TABLE_STATUS_COL_WIDTH,
  tableCenteredCellStyle,
  tableMinWidth,
  tableRowStyle
} from '@/components/common';
import { useAuthContext } from '@/features/auth/context/AuthContext';
import { formatObjectLabel } from '@/features/price-lists/objectDisplay';
import { statusConfig } from '@/features/work-packages/workPackageDisplay';
import { usePageSize } from '@/utils/usePageSize';
import { useSavedMessage } from '@/utils/useSavedMessage';
import { useUrlNullableParam, useUrlPageParam, useUrlTextParam } from '@/utils/useUrlFilters';

export const WorkPackagesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [savedMessage, setSavedMessage] = useSavedMessage();
  const [page, setPage] = useUrlPageParam();
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useUrlTextParam('search');
  const [statusFilter, setStatusFilter] = useUrlTextParam('status', 'ALL');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [objectFilter, setObjectFilter] = useUrlNullableParam('objectId');
  const [counterpartyFilter, setCounterpartyFilter] = useUrlNullableParam('counterpartyOrgId');

  // page dolazi iz URL-a (useUrlPageParam) — na inicijalnom mountu (npr. povratak
  // s detalja preko BackButton-a) NE smije se resetirati na 1, samo kad korisnik
  // stvarno promijeni neki filter nakon toga.
  const isFirstFilterEffect = useRef(true);
  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    setPage(1);
  }, [debouncedSearch, statusFilter, objectFilter, counterpartyFilter, pageSize]);

  // refetchOnMount: 'always' — lista mora biti svježa svaki put kad se uđe na
  // ekran, ne samo kad je staleTime istekao (npr. netom kreirani radni paket mora
  // odmah biti vidljiv, ne tek za 5 min).
  const { data, isLoading } = useSearchAllWorkPackages(
    {
      page,
      pageSize,
      search: debouncedSearch.trim() || undefined,
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      objectId: objectFilter ?? undefined,
      counterpartyOrgId: counterpartyFilter ?? undefined
    },
    { query: { refetchOnMount: 'always' } }
  );
  const pageItems = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const statusOptions = [
    { value: 'ALL', label: t('common.filters.allStatuses') },
    ...Object.values(WorkPackageStatus).map((status) => ({
      value: status,
      label: t(statusConfig[status].labelKey)
    }))
  ];

  const { data: objectsData } = useListObjects();
  const objectOptions = useMemo(
    () =>
      (objectsData ?? [])
        .filter((object) => object.isActive)
        .map((object) => ({
          value: object.id ?? '',
          label: formatObjectLabel(object)
        }))
        .filter((option) => option.value),
    [objectsData, t]
  );

  const { data: connectionsData } = useListOrganisationConnections();
  const counterpartyOptions = useMemo(
    () =>
      (connectionsData ?? [])
        .filter((c) => c.isActive)
        .map((c) => {
          const isOrgA = c.orgAId === user?.organisationId;
          return {
            value: (isOrgA ? c.orgBId : c.orgAId) ?? '',
            label: (isOrgA ? c.orgBName : c.orgAName) ?? '—'
          };
        })
        .filter((option) => option.value),
    [connectionsData, user?.organisationId]
  );
  const orgNameById = useMemo(() => {
    const map = new Map<string, string>();
    (connectionsData ?? []).forEach((c) => {
      if (c.orgAId && c.orgAName) map.set(c.orgAId, c.orgAName);
      if (c.orgBId && c.orgBName) map.set(c.orgBId, c.orgBName);
    });
    return map;
  }, [connectionsData]);

  const paginationLabel = t('workPackages.showing', {
    from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
    to: Math.min(page * pageSize, totalElements),
    total: totalElements
  });

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const columns: [string, number, ('center' | undefined)?][] = [
    ['workPackages.colCode', TABLE_CODE_COL_WIDTH],
    ['workPackages.colName', 230],
    ['workPackages.colClient', 160],
    ['workPackages.colObject', TABLE_OBJECT_COL_WIDTH],
    ['workPackages.colStatus', TABLE_STATUS_COL_WIDTH, 'center']
  ];
  const tableMinWidthPx = tableMinWidth(
    [...columns.map(([, width]) => width), TABLE_ACTION_COL_WIDTH],
    20
  );

  return (
    <Container size="lg" px={0}>
      <Stack gap={18}>
        {savedMessage && (
          <StatusBanner
            tone="success"
            message={savedMessage}
            onDismiss={() => setSavedMessage(null)}
          />
        )}

        <Group justify="space-between" wrap="nowrap" visibleFrom="sm">
          <PageHeading title={t('workPackages.title')} subtitle={t('workPackages.subtitle')} />
          <Button onClick={() => navigate('/work-packages/new')}>
            + {t('workPackages.newWorkPackage')}
          </Button>
        </Group>

        <Stack gap={12} hiddenFrom="sm">
          <PageHeading title={t('workPackages.title')} subtitle={t('workPackages.subtitle')} />
          <MobileFab onClick={() => navigate('/work-packages/new')}>
            + {t('workPackages.newWorkPackage')}
          </MobileFab>
        </Stack>

        <Box hiddenFrom="sm">
          <MobileListFilters
            search={
              <SearchField
                hideLabel
                placeholder={t('workPackages.searchPlaceholder')}
                value={search}
                onChange={(event) => handleSearchChange(event.currentTarget.value)}
              />
            }
            filters={[
              {
                key: 'status',
                label: t('common.filters.status'),
                options: statusOptions,
                value: statusFilter,
                onChange: (value) => setStatusFilter(value ?? 'ALL'),
                emptyValue: 'ALL',
                variant: 'buttons'
              },
              {
                key: 'counterparty',
                label: t('workPackages.filterCounterparty'),
                options: counterpartyOptions,
                value: counterpartyFilter,
                onChange: setCounterpartyFilter
              },
              {
                key: 'object',
                label: t('workPackages.filterObject'),
                options: objectOptions,
                value: objectFilter,
                onChange: setObjectFilter
              }
            ]}
          />
        </Box>

        <Group gap={16} align="flex-end" wrap="wrap" visibleFrom="sm">
          <Box w={280}>
            <SearchField
              hideLabel
              placeholder={t('workPackages.searchPlaceholder')}
              value={search}
              onChange={(event) => handleSearchChange(event.currentTarget.value)}
            />
          </Box>
          <Box w={220}>
            <SelectField
              hideLabel
              data={statusOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value ?? 'ALL')}
              allowDeselect={false}
            />
          </Box>
          <Box w={200}>
            <SelectField
              hideLabel
              placeholder={t('workPackages.filterCounterparty')}
              data={counterpartyOptions}
              value={counterpartyFilter}
              onChange={setCounterpartyFilter}
              clearable
            />
          </Box>
          <Box w={200}>
            <SelectField
              hideLabel
              placeholder={t('workPackages.filterObject')}
              data={objectOptions}
              value={objectFilter}
              onChange={setObjectFilter}
              clearable
            />
          </Box>
        </Group>

        <Box visibleFrom="sm">
          <CardSection>
            {!isLoading && pageItems.length === 0 && (
              <Center py={40}>
                <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                  {t('workPackages.emptyState')}
                </Text>
              </Center>
            )}

            {pageItems.length > 0 && (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ minWidth: tableMinWidthPx }}>
                    <div
                      className="table-header-row"
                      style={{
                        ...tableRowStyle,
                        padding: '12px 20px',
                        backgroundColor: 'var(--color-bg-canvas)',
                        borderBottom: '1px solid var(--color-border-soft)'
                      }}
                    >
                      {columns.map(([key, width, align]) => (
                        <Text
                          key={key}
                          fz={10}
                          fw={600}
                          w={width}
                          ta={align}
                          style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                        >
                          {t(key)}
                        </Text>
                      ))}
                      <Text
                        fz={10}
                        fw={600}
                        w={TABLE_ACTION_COL_WIDTH}
                        style={{
                          ...tableCenteredCellStyle,
                          color: 'var(--color-text-muted)',
                          letterSpacing: 0.4
                        }}
                      >
                        {t('workPackages.colAction')}
                      </Text>
                    </div>

                    {pageItems.map((wp, index) => {
                      const config = statusConfig[wp.status ?? 'DRAFT'];
                      return (
                        <div
                          key={wp.id}
                          style={{
                            ...tableRowStyle,
                            padding: '14px 20px',
                            borderTop:
                              index === 0 ? undefined : '1px solid var(--color-border-soft)'
                          }}
                        >
                          <Text
                            fz={13.5}
                            fw={600}
                            w={TABLE_CODE_COL_WIDTH}
                            style={{ color: 'var(--color-text-primary)' }}
                          >
                            {wp.code}
                          </Text>
                          <Text
                            fz={13}
                            w={230}
                            style={{ color: 'var(--color-text-secondary)' }}
                            truncate
                          >
                            {wp.name}
                          </Text>
                          <Text
                            fz={13}
                            w={160}
                            style={{ color: 'var(--color-text-secondary)' }}
                            truncate
                          >
                            {(wp.clientOrgId && orgNameById.get(wp.clientOrgId)) ?? '—'}
                          </Text>
                          <Text
                            fz={13}
                            w={TABLE_OBJECT_COL_WIDTH}
                            style={{
                              color: 'var(--color-text-secondary)',
                              cursor: wp.objectId ? 'pointer' : undefined
                            }}
                            truncate
                            role={wp.objectId ? 'button' : undefined}
                            tabIndex={wp.objectId ? 0 : undefined}
                            onClick={() =>
                              wp.objectId && navigate(`/templates/object-registry/${wp.objectId}`)
                            }
                            onKeyDown={(event) => {
                              if ((event.key === 'Enter' || event.key === ' ') && wp.objectId)
                                navigate(`/templates/object-registry/${wp.objectId}`);
                            }}
                          >
                            {wp.objectName ?? '—'}
                          </Text>
                          <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                            <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} />
                          </div>
                          <div style={{ ...tableCenteredCellStyle, width: TABLE_ACTION_COL_WIDTH }}>
                            <RowActionsMenu
                              detailsHref={`/work-packages/${wp.id}`}
                              historyHref={`/work-packages/${wp.id}/history`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <ListPagination
                  label={paginationLabel}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                />
              </>
            )}
          </CardSection>
        </Box>

        <Stack hiddenFrom="sm" gap={12}>
          {!isLoading && pageItems.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('workPackages.emptyState')}
              </Text>
            </Center>
          )}

          {pageItems.length > 0 && (
            <>
              {pageItems.map((wp) => {
                const config = statusConfig[wp.status ?? 'DRAFT'];
                return (
                  <Stack
                    key={wp.id}
                    gap={10}
                    p={16}
                    style={{
                      borderRadius: 12,
                      border: '1px solid var(--color-border-default)',
                      backgroundColor: 'var(--color-bg-surface)'
                    }}
                  >
                    <Group justify="space-between" align="flex-start" wrap="nowrap">
                      <Stack gap={2} style={{ flex: 1 }}>
                        <Text fz={14.5} fw={700} style={{ color: 'var(--color-text-primary)' }}>
                          {wp.name}
                        </Text>
                        <Text fz={12} style={{ color: 'var(--color-text-muted)' }}>
                          {wp.code}
                          {wp.clientOrgId && orgNameById.get(wp.clientOrgId)
                            ? ` · ${orgNameById.get(wp.clientOrgId)}`
                            : ''}
                          {wp.objectName && (
                            <>
                              {' · '}
                              <Text
                                span
                                inherit
                                role="button"
                                tabIndex={0}
                                style={{ cursor: 'pointer' }}
                                onClick={() =>
                                  navigate(`/templates/object-registry/${wp.objectId}`)
                                }
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ')
                                    navigate(`/templates/object-registry/${wp.objectId}`);
                                }}
                              >
                                {wp.objectName}
                              </Text>
                            </>
                          )}
                        </Text>
                      </Stack>
                      <RowActionsMenu
                        detailsHref={`/work-packages/${wp.id}`}
                        historyHref={`/work-packages/${wp.id}/history`}
                      />
                    </Group>
                    <StatusPill label={t(config.labelKey)} bg={config.bg} fg={config.fg} />
                  </Stack>
                );
              })}

              <ListPagination
                label={paginationLabel}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                bordered={false}
              />
            </>
          )}
        </Stack>
      </Stack>
    </Container>
  );
};
